import * as THREE from 'three';
import { commonGLSL } from '../shaders/common';

const vert = /* glsl */ `
in vec4 aSeed; // xyz random offset in box (0..1), w random
uniform vec3 uCamPos;
uniform vec3 uBox;
uniform float uTime;
uniform vec3 uVel;        // fall velocity (m/s), includes wind
uniform float uSnow;      // 0 rain, 1 snow
uniform float uCount;     // active fraction
uniform float uStreak;    // streak length scale (s)
out vec2 vQuad;
out float vAlpha;
out vec3 vWorld;
out float vViewZ;
void main() {
  if (aSeed.w > uCount) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
  vec3 v = uVel * (0.8 + 0.4 * aSeed.w);
  vec3 p = aSeed.xyz * uBox + v * uTime;
  // snow: swirling
  p.x += uSnow * sin(uTime * (0.7 + aSeed.w) + aSeed.y * 30.0) * 0.6;
  p.z += uSnow * cos(uTime * (0.6 + aSeed.w * 0.7) + aSeed.x * 25.0) * 0.6;
  // wrap into a box centered on the camera
  vec3 origin = uCamPos - uBox * 0.5;
  p = origin + mod(p - origin, uBox);
  vec3 toCam = normalize(uCamPos - p);
  vec3 dir = normalize(v);
  float len = mix(length(v) * uStreak, 0.0, uSnow);
  float w = mix(0.0045, 0.022 + 0.014 * aSeed.w, uSnow);
  vec3 side = normalize(cross(dir, toCam));
  vec3 up = mix(dir, normalize(cross(toCam, side)), uSnow);
  vec2 q = position.xy; // -1..1
  vec3 wp = p + side * q.x * w + up * q.y * (mix(len * 0.5, w, uSnow));
  vWorld = wp;
  vQuad = q;
  float d = length(wp - uCamPos);
  vAlpha = smoothstep(0.3, 1.5, d) * (1.0 - smoothstep(uBox.x * 0.35, uBox.x * 0.5, d));
  vec4 mv = viewMatrix * vec4(wp, 1.0);
  vViewZ = -mv.z;
  gl_Position = projectionMatrix * mv;
}
`;

const frag = /* glsl */ `
uniform sampler2D uSceneDepth;
uniform sampler2D uSkyIrr;
uniform vec2 uResolution;
uniform float uNear;
uniform float uFar;
uniform float uSnow;
uniform float uIntensity;
uniform float uLightning;
in vec2 vQuad;
in float vAlpha;
in vec3 vWorld;
in float vViewZ;
out vec4 fragColor;
float linearDepth(float d) {
  float z = d * 2.0 - 1.0;
  return (2.0 * uNear * uFar) / (uFar + uNear - z * (uFar - uNear));
}
void main() {
  vec2 suv = gl_FragCoord.xy / uResolution;
  float sceneZ = linearDepth(texture(uSceneDepth, suv).r);
  if (vViewZ > sceneZ) discard;
  float soft = clamp((sceneZ - vViewZ) * 2.0, 0.0, 1.0);
  vec3 amb = texelFetch(uSkyIrr, ivec2(0, 0), 0).rgb * 0.282095 * PI;
  float shape;
  if (uSnow > 0.5) {
    float r = length(vQuad);
    shape = smoothstep(1.0, 0.3, r);
  } else {
    shape = (1.0 - abs(vQuad.x)) * smoothstep(1.0, 0.6, abs(vQuad.y));
  }
  vec3 col = amb * mix(1.2, 2.2, uSnow) + vec3(uLightning * 3.0);
  float a = shape * vAlpha * soft * mix(0.22, 0.85, uSnow) * uIntensity;
  fragColor = vec4(col * a, a);
}
`;

/** Camera-following rain / snow particles rendered into the HDR composite with soft depth occlusion. */
export class Precipitation {
  readonly mesh: THREE.Mesh;
  readonly scene = new THREE.Scene();
  readonly uniforms: Record<string, THREE.IUniform>;
  private readonly count: number;

  constructor(count = 24000) {
    this.count = count;
    const g = new THREE.InstancedBufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute([-1, -1, 0, 1, -1, 0, 1, 1, 0, -1, 1, 0], 3));
    g.setIndex([0, 1, 2, 0, 2, 3]);
    const seeds = new Float32Array(count * 4);
    for (let i = 0; i < count * 4; i++) seeds[i] = Math.random();
    g.setAttribute('aSeed', new THREE.InstancedBufferAttribute(seeds, 4));
    g.instanceCount = count;
    this.uniforms = {
      uCamPos: { value: new THREE.Vector3() },
      uBox: { value: new THREE.Vector3(36, 24, 36) },
      uTime: { value: 0 },
      uVel: { value: new THREE.Vector3(0, -8, 0) },
      uSnow: { value: 0 },
      uCount: { value: 0 },
      uStreak: { value: 0.035 },
      uSceneDepth: { value: null },
      uSkyIrr: { value: null },
      uResolution: { value: new THREE.Vector2(1, 1) },
      uNear: { value: 0.3 },
      uFar: { value: 400000 },
      uIntensity: { value: 1 },
      uLightning: { value: 0 },
    };
    const mat = new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: vert,
      fragmentShader: commonGLSL + frag,
      uniforms: this.uniforms,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      blending: THREE.CustomBlending,
      blendSrc: THREE.OneFactor,
      blendDst: THREE.OneMinusSrcAlphaFactor,
    });
    this.mesh = new THREE.Mesh(g, mat);
    this.mesh.frustumCulled = false;
    this.scene.add(this.mesh);
  }

  get active() {
    return (this.uniforms.uCount.value as number) > 0.001;
  }

  update(time: number, cam: THREE.PerspectiveCamera, rain: number, snow: number, wind: THREE.Vector2, depth: THREE.Texture,
    skyIrr: THREE.Texture, res: THREE.Vector2, lightning: number) {
    const u = this.uniforms;
    const isSnow = snow > rain ? 1 : 0;
    const amount = Math.max(rain, snow);
    u.uSnow.value = isSnow;
    u.uCount.value = Math.min(1, amount);
    u.uTime.value = time;
    u.uCamPos.value.copy(cam.position);
    const fall = isSnow ? 1.1 : 8.5;
    u.uVel.value.set(wind.x * (isSnow ? 0.9 : 0.6), -fall, wind.y * (isSnow ? 0.9 : 0.6));
    u.uBox.value.set(isSnow ? 44 : 34, isSnow ? 28 : 22, isSnow ? 44 : 34);
    u.uSceneDepth.value = depth;
    u.uSkyIrr.value = skyIrr;
    u.uResolution.value.copy(res);
    u.uNear.value = cam.near;
    u.uFar.value = cam.far;
    u.uLightning.value = lightning;
    void this.count;
  }
}
